// ---------------------------------------------------------------------------
// Single source of truth for the site's Firestore schema.
// All screens and rules must agree with the shapes defined here.
// ---------------------------------------------------------------------------
import { z } from 'zod';

export const COLLECTIONS = {
  siteSettings: 'siteSettings',
  courses: 'courses',
  chapters: 'chapters',
  sections: 'sections',
  resources: 'resources',
  tools: 'tools',
  users: 'users'
} as const;

export const ADMIN_EMAILS = ['ahmedomarz2020@gmail.com', 'asma.mughrabi@gmail.com'] as const;

export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
export const UPLOAD_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.gif,.heic';
export const UPLOAD_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif'
] as const;

const str = (max: number, opts?: { min?: number }) =>
  z
    .string()
    .trim()
    .max(max)
    .refine(v => opts?.min !== undefined ? v.length >= opts.min : true, 'required')
    .refine(v => !/[<>&]/.test(v), 'contains forbidden characters');

const httpsUrl = z.string().trim().regex(/^https:\/\/[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]{1,512}$/);
const youtubeId = z.string().regex(/^[A-Za-z0-9_-]{11}$/);
const storagePath = z.string().regex(/^uploads\/[A-Za-z0-9_-]{1,64}\/[A-Za-z0-9 ._()+-]{1,255}$/);

export const orderSchema = z.number().int().min(0).lt(100000);
export const hiddenSchema = z.boolean();

export const courseSchema = z.object({
  order: orderSchema,
  code: str(30, { min: 1 }),
  name: str(100, { min: 1 }),
  tagline: str(300),
  glyph: str(4),
  accent: str(7),
  tint: str(20).optional(),
  deep: str(20).optional(),
  hidden: hiddenSchema,
  updatedAt: z.number().optional()
});
export type CourseDoc = z.infer<typeof courseSchema>;

export const chapterSchema = z.object({
  courseId: str(64, { min: 1 }),
  order: orderSchema,
  num: str(10),
  title: str(300, { min: 1 }),
  blurb: str(500),
  hidden: hiddenSchema,
  updatedAt: z.number().optional()
});
export type ChapterDoc = z.infer<typeof chapterSchema>;

export const sectionSchema = z.object({
  chapterId: str(64, { min: 1 }),
  order: orderSchema,
  title: str(300, { min: 1 }),
  summary: str(500),
  hidden: hiddenSchema,
  updatedAt: z.number().optional()
});
export type SectionDoc = z.infer<typeof sectionSchema>;

export const resourceKind = z.enum(['video', 'pdf', 'file', 'link']);
export type ResourceKind = z.infer<typeof resourceKind>;

export const resourceSchema = z
  .object({
    sectionId: str(64, { min: 1 }),
    courseId: str(64, { min: 1 }),
    order: orderSchema,
    kind: resourceKind,
    title: str(300, { min: 1 }),
    meta: str(300).optional(),
    url: httpsUrl.optional(),
    youtubeId: youtubeId.optional(),
    storagePath: storagePath.optional(),
    fileName: str(255).optional(),
    hidden: hiddenSchema,
    updatedAt: z.number().optional()
  })
  .superRefine((r, ctx) => {
    if (r.kind === 'link') {
      if (!r.url) ctx.addIssue({ code: 'custom', path: ['url'], message: 'Links need an https URL' });
    } else if (r.kind === 'video') {
      if (!r.youtubeId && !r.url)
        ctx.addIssue({ code: 'custom', path: ['youtubeId'], message: 'Video needs a YouTube link' });
    } else if (r.kind === 'pdf' || r.kind === 'file') {
      if (!r.storagePath && !r.url)
        ctx.addIssue({ code: 'custom', path: ['storagePath'], message: 'Add a file or an https URL' });
    }
  });
export type ResourceDoc = z.infer<typeof resourceSchema>;

export const toolSchema = z.object({
  order: orderSchema,
  name: str(100, { min: 1 }),
  tag: str(30),
  blurb: str(500),
  url: httpsUrl,
  glyph: str(4).optional(),
  iconBg: str(40).optional(),
  iconFg: str(40).optional(),
  hidden: hiddenSchema,
  updatedAt: z.number().optional()
});
export type ToolDoc = z.infer<typeof toolSchema>;

export const siteSettingsSchema = z.object({
  appName: str(100, { min: 1 }),
  tagline: str(300),
  footerText: str(200),
  gamified: z.boolean(),
  published: z.boolean().optional(),
  updatedAt: z.number().optional()
});
export type SiteSettingsDoc = z.infer<typeof siteSettingsSchema>;

export const XP_PER_LEVEL = 500;

/** Gamified level math (mirrors design.html: level=floor(xp/500)+1). */
export function gamify(xp: number) {
  const level = Math.floor(xp / XP_PER_LEVEL) + 1;
  const levelPct = Math.round(((xp % XP_PER_LEVEL) / XP_PER_LEVEL) * 100);
  const xpToNext = Math.max(0, XP_PER_LEVEL - (xp % XP_PER_LEVEL));
  const label =
    level >= 4 ? 'Series specialist' : level === 3 ? 'Steady integrator' : 'Getting warmed up';
  return { level, nextLevel: level + 1, levelPct, xpToNext, label };
}

export const DEFAULT_SETTINGS: SiteSettingsDoc = {
  appName: 'MasterStax',
  tagline: 'Student Resources Hub',
  footerText: '© 2026 MikaLabs · MasterStax. All rights reserved.',
  gamified: true
};

export const userProfileSchema = z.object({
  email: str(320),
  name: str(120).optional(),
  courseKey: str(64).optional(),
  done: z.array(str(300)).max(2000).default([]),
  joinedAt: z.number().optional(),
  lastActive: z.number().optional(),
  activeDays: z.record(z.string(), z.number()).optional(),
  streakDays: z.number().int().min(0).optional(),
  theme: z.enum(['light', 'dark']).optional()
});
export type UserProfile = z.infer<typeof userProfileSchema>;

// ---------- helpers ----------

export function docId(prefix: string, seq: number): string {
  return `${prefix}-${seq}`;
}

/** Parse a YouTube id out of any common youtube.com / youtu.be URL. */
export function parseYoutubeId(raw: string): string | null {
  const t = raw.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(t)) return t;
  const m = t.match(/^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

export function isValidKind(k: string): k is ResourceKind {
  return k === 'video' || k === 'pdf' || k === 'file' || k === 'link';
}

/** Remove keys the schema doesn't know about + empty optionals before writing. */
export function toWritable<T extends Record<string, unknown>>(obj: T, allowed: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of allowed) {
    const v = (obj as Record<string, unknown>)[k];
    if (v === undefined) continue;
    if (typeof v === 'string' && v === '') continue;
    out[k] = v;
  }
  return out;
}
